import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { readFileAsDataURL } from "@/lib/month";
import { formatDateTime } from "@/lib/format";
import { Palmtree, Loader2, Paperclip, Check, X, FileText, Plus } from "lucide-react";

const LEAVE_BADGE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};

function LeaveChip({ status }) {
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${LEAVE_BADGE[status] || LEAVE_BADGE.pending}`} data-testid="leave-status-chip">{status}</span>;
}

function fmtDate(d) {
  if (!d) return "-";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function AttachmentLink({ path, dark }) {
  if (!path) return <span className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>—</span>;
  const isPdf = path.toLowerCase().endsWith(".pdf");
  const openFile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get("/files/sign", { params: { path } });
      window.open(`${API}/files/${path}?st=${data.st}`, "_blank");
    } catch {
      toast.error("Could not open attachment");
    }
  };
  return (
    <a href={`${API}/files/${path}`} onClick={openFile}
      className={`inline-flex items-center gap-1 text-xs hover:underline ${dark ? "text-indigo-300" : "text-indigo-600"}`} data-testid="leave-attachment-link">
      {isPdf ? <FileText className="h-3.5 w-3.5" /> : <Paperclip className="h-3.5 w-3.5" />} {isPdf ? "PDF" : "Image"}
    </a>
  );
}

export default function LeavePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dark = isAdmin;
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ type: "izin", start_date: "", end_date: "", reason: "", attachment: null });
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = isAdmin ? (filter === "all" ? "/leaves" : `/leaves?status=${filter}`) : "/leaves/my";
      const { data } = await api.get(url);
      setLeaves(data.leaves);
    } catch { toast.error("Failed to load leave requests"); }
    finally { setLoading(false); }
  }, [isAdmin, filter]);

  useEffect(() => { load(); }, [load]);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("File too large (max 3 MB)");
    const dataUrl = await readFileAsDataURL(f);
    setForm((prev) => ({ ...prev, attachment: dataUrl }));
    setFileName(f.name);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error("Select start and end dates");
    setSaving(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setFormOpen(false);
      setForm({ type: "izin", start_date: "", end_date: "", reason: "", attachment: null });
      setFileName("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const review = async (id, status) => {
    try {
      await api.patch(`/leaves/${id}/review`, { status });
      toast.success(`Leave ${status}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const card = dark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white";
  const sub = dark ? "text-slate-400" : "text-slate-500";

  return (
    <div className={`min-h-screen ${dark ? "bg-[#1F1B16] dark-scroll text-slate-100" : "bg-slate-50"}`}>
      <AppNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="leave-page">
        <div>
          <h1 className={`font-display text-2xl font-bold flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
            <Palmtree className="h-6 w-6 text-emerald-500" /> Leave & Permission
          </h1>
          <p className={`text-sm ${sub}`}>{isAdmin ? "Review staff leave, sick, and permission requests." : "Submit leave (cuti), sick (sakit), or permission (izin) requests."}</p>
        </div>

        {!isAdmin && (
          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-leave-btn"><Plus className="h-4 w-4" /> Create leave request</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>New leave request</DialogTitle>
                <DialogDescription>Submit cuti, sakit, or izin with an optional supporting file (photo / PDF).</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="leave-form">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cuti">Cuti</SelectItem>
                    <SelectItem value="sakit">Sakit</SelectItem>
                    <SelectItem value="izin">Izin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="leave-start-input" />
              </div>
              <div className="space-y-1.5">
                <Label>End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required data-testid="leave-end-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Tell your admin why…" className="resize-none h-20" data-testid="leave-reason-input" />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-indigo-600" data-testid="leave-attachment-picker">
                <Paperclip className="h-4 w-4" />
                {fileName || "Attach supporting file (photo / PDF, max 3 MB)"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={pickFile} />
              </label>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="leave-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}
              </Button>
            </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        <div className={`rounded-xl border overflow-hidden ${card}`}>
          <div className={`px-4 py-3 border-b flex items-center justify-between ${dark ? "border-slate-800" : "border-slate-100"}`}>
            <h3 className={`font-display font-semibold ${dark ? "text-white" : "text-slate-900"}`}>{isAdmin ? "All requests" : "My requests"}</h3>
            {isAdmin && (
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-36 bg-slate-900 border-slate-700 text-slate-200" data-testid="leave-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
          ) : leaves.length === 0 ? (
            <p className={`text-sm py-10 text-center ${sub}`}>No leave requests yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leave-table">
                <thead>
                  <tr className={`border-b text-left ${dark ? "border-slate-800 text-slate-400" : "border-slate-100 text-slate-500"}`}>
                    {isAdmin && <th className="p-3 font-medium">Staff</th>}
                    <th className="p-3 font-medium">Type</th>
                    <th className="p-3 font-medium">Dates</th>
                    <th className="p-3 font-medium">Reason</th>
                    <th className="p-3 font-medium">File</th>
                    <th className="p-3 font-medium">Status</th>
                    {isAdmin && <th className="p-3 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((l) => (
                    <tr key={l.id} className={`border-b ${dark ? "border-slate-800/60 hover:bg-slate-800/30" : "border-slate-50 hover:bg-slate-50"}`} data-testid={`leave-row-${l.id}`}>
                      {isAdmin && (
                        <td className="p-3">
                          <div className={`font-medium ${dark ? "text-white" : "text-slate-900"}`}>{l.user_name}</div>
                          <div className={`text-xs ${sub}`}>{l.user_email}</div>
                        </td>
                      )}
                      <td className={`p-3 capitalize ${dark ? "text-slate-200" : "text-slate-700"}`}>{l.type}</td>
                      <td className={`p-3 whitespace-nowrap ${dark ? "text-slate-300" : "text-slate-600"}`}>{fmtDate(l.start_date)} – {fmtDate(l.end_date)}</td>
                      <td className={`p-3 max-w-[220px] truncate ${sub}`} title={l.reason}>{l.reason || "—"}</td>
                      <td className="p-3"><AttachmentLink path={l.attachment_path} dark={dark} /></td>
                      <td className="p-3">
                        <LeaveChip status={l.status} />
                        {l.reviewed_by && <div className={`text-[10px] mt-0.5 ${sub}`}>by {l.reviewed_by}</div>}
                      </td>
                      {isAdmin && (
                        <td className="p-3 text-right">
                          {l.status === "pending" ? (
                            <div className="inline-flex gap-1.5">
                              <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => review(l.id, "approved")} data-testid={`approve-${l.id}`}><Check className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="destructive" className="h-7" onClick={() => review(l.id, "rejected")} data-testid={`reject-${l.id}`}><X className="h-3.5 w-3.5" /></Button>
                            </div>
                          ) : (
                            <span className={`text-xs ${sub}`}>{formatDateTime(l.reviewed_at)}</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
